/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import type { CalendarEvent } from '@repo/common';

export function parseDate(input: string | Date): Date {
  if (typeof input === 'string') {
    return parseDateStr(input); // Use parseDateStr for string inputs
  } else if (input instanceof Date) {
    return input; // Return the Date object as-is
  } else {
    throw new Error('Invalid input: Expected a string or Date object');
  }
}

function parseDateStr(input: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    // Simple date (YYYY-MM-DD)
    const [year, month, day] = input.split('-').map(Number);
    return new Date(year, month - 1, day); // Interpret as local time
  } else {
    // ISO datetime (e.g., 2025-01-13T15:30:00Z)
    return new Date(input); // JavaScript handles this as either UTC or with timezone offset
  }
}

/**
 * Safely convert a date value to ISO string format
 * Handles Date objects, string dates, undefined, and null values
 * @param date - The date value to convert (Date, string, undefined, or null)
 * @param fallbackDate - Optional fallback date to use if input is invalid (defaults to current date)
 * @param preserveUndefined - If true, return undefined for null/undefined inputs instead of fallback
 * @returns ISO string representation of the date, or undefined if preserveUndefined is true and input is null/undefined
 */
export function safeToISOString(
  date: Date | string | undefined | null,
  fallbackDate: Date,
  preserveUndefined: false
): string;
export function safeToISOString(
  date: Date | string | undefined | null,
  fallbackDate?: Date,
  preserveUndefined?: boolean
): string | undefined;
export function safeToISOString(
  date: Date | string | undefined | null,
  fallbackDate?: Date,
  preserveUndefined: boolean = false
): string | undefined {
  if (!date) {
    if (preserveUndefined) {
      return undefined;
    }
    return (fallbackDate || new Date()).toISOString();
  }

  if (typeof date === 'string') {
    // If it's already a string, try to parse it and convert back to ensure it's valid
    const parsed = new Date(date);
    if (isNaN(parsed.getTime())) {
      if (preserveUndefined) {
        return undefined;
      }
      return (fallbackDate || new Date()).toISOString();
    }
    return parsed.toISOString();
  }

  if (date instanceof Date) {
    return date.toISOString();
  }

  if (preserveUndefined) {
    return undefined;
  }
  return (fallbackDate || new Date()).toISOString();
}

export function setEndOfDay(date: Date): Date {
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999); // Set to 23:59:59.999
  return endOfDay;
}

function _formatLocalDate(date: Date | undefined, addTime: boolean): string {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    ...(addTime
      ? {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }
      : {})
  };
  try {
    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.log('Failed to format local date with toLocaleDateString, using fallback:', error);
    // Fallback to a simple date format
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    const month = months[date.getMonth()];
    const day = date.getDate();

    if (!addTime) {
      return `${month} ${day}`;
    }

    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${month} ${day}, ${displayHours}:${displayMinutes} ${ampm}`;
  }
}

export function formatLocalDate(date: Date | undefined): string {
  return _formatLocalDate(date, true);
}

export function formatLocalDateOnly(date: Date | undefined): string {
  return _formatLocalDate(date, false);
}

/**
 * Format date like "Thu, Jul 17 • 7:28 PM"
 * @param date - The date to format
 * @returns Formatted date string
 */
export function formatMeetingDate(date: Date | string | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  };
  const datePart = d.toLocaleDateString('en-US', dateOptions);

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };
  const timePart = d.toLocaleTimeString('en-US', timeOptions);

  return `${datePart} • ${timePart}`;
}

const DAY = 1;
const WEEK = 7;
const MONTH = 30;

export const getTimeRange = (
  timeGrain: 'day' | 'week' | 'month' | 'all' | undefined,
  i = 0
): [Date, Date] | undefined => {
  let startRange: Date;
  let endRange: Date;
  switch (timeGrain) {
    case 'day':
      startRange = new Date();
      startRange.setDate(startRange.getDate() - (i + 1) * DAY);
      endRange = new Date();
      endRange.setDate(endRange.getDate() - i * DAY);
      return [startRange, endRange];
    case 'week':
      startRange = new Date();
      startRange.setDate(startRange.getDate() - (i + 1) * WEEK);
      endRange = new Date();
      endRange.setDate(endRange.getDate() - i * WEEK);
      return [startRange, endRange];
    case 'month':
      startRange = new Date();
      startRange.setDate(startRange.getDate() - (i + 1) * MONTH);
      endRange = new Date();
      endRange.setDate(endRange.getDate() - i * MONTH);
      return [startRange, endRange];
    case 'all':
      startRange = new Date();
      startRange.setDate(startRange.getDate() - (i + 1) * 12 * MONTH);
      endRange = new Date();
      endRange.setDate(endRange.getDate() - i * 12 * MONTH);
      return [startRange, endRange];
    default:
      return undefined;
  }
};

export const timeAgo = (time: number | string | Date) => {
  switch (typeof time) {
    case 'number':
      break;
    case 'string':
      time = +new Date(time);
      break;
    case 'object':
      if (time.constructor === Date) time = time.getTime();
      break;
    default:
      time = +new Date();
  }

  let seconds = (+new Date() - Number(time)) / 1000;
  const isNegative = seconds < 0;
  if (isNegative) {
    seconds = Math.abs(seconds);
  }

  if (seconds < 10) {
    return 'now';
  }
  if (seconds < 60) {
    return isNegative ? 'in a moment' : 'just now';
  }
  if (seconds < 120) {
    return isNegative ? 'in 1m' : '1m ago';
  }
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return isNegative ? `in ${minutes}m` : `${minutes}m ago`;
  }
  if (seconds < 7200) {
    return isNegative ? 'in 1h' : '1h ago';
  }
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return isNegative ? `in ${hours}h` : `${hours}h ago`;
  }
  if (seconds < 172800) {
    return isNegative ? 'tomorrow' : 'yesterday';
  }
  if (seconds < 604800) {
    const days = Math.floor(seconds / 86400);
    return isNegative ? `in ${days}d` : `${days}d ago`;
  }
  if (seconds < 1209600) {
    return isNegative ? 'next week' : 'last week';
  }
  if (seconds < 2419200) {
    const weeks = Math.floor(seconds / 604800);
    return isNegative ? `in ${weeks}w` : `${weeks}w ago`;
  }
  if (seconds < 4838400) {
    return isNegative ? 'next month' : 'last month';
  }
  if (seconds < 29030400) {
    const months = Math.floor(seconds / 2419200);
    return isNegative ? `in ${months}mo` : `${months}mo ago`;
  }

  // For very old items, just show the year
  const years = Math.floor(seconds / 29030400);
  if (years === 1) {
    return isNegative ? 'next year' : 'last year';
  }
  return isNegative ? `in ${years}y` : `${years}y ago`;
};

export function formatTimeRemaining(timeStr: string | undefined): string | undefined {
  if (!timeStr) return undefined;

  // Extract the number from strings like "In -430 minutes" or "In 30 minutes"
  const match = timeStr.match(/In (-?\d+) minutes?/);
  if (!match) return timeStr; // Return original if format doesn't match

  const minutes = parseInt(match[1]);

  if (minutes < 0) {
    // Past event
    const absMinutes = Math.abs(minutes);
    if (absMinutes < 60) {
      return `${absMinutes} min ago`;
    } else if (absMinutes < 1440) {
      // Less than 24 hours
      const hours = Math.floor(absMinutes / 60);
      const remainingMins = absMinutes % 60;
      if (remainingMins === 0) {
        return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
      } else {
        return hours === 1 ? `1h ${remainingMins}m ago` : `${hours}h ${remainingMins}m ago`;
      }
    } else {
      // More than 24 hours ago
      const days = Math.floor(absMinutes / 1440);
      return days === 1 ? '1 day ago' : `${days} days ago`;
    }
  } else {
    // Future event
    if (minutes < 60) {
      return minutes === 1 ? 'In 1 min' : `In ${minutes} mins`;
    } else if (minutes < 1440) {
      // Less than 24 hours
      const hours = Math.floor(minutes / 60);
      const remainingMins = minutes % 60;
      if (remainingMins === 0) {
        return hours === 1 ? 'In 1 hour' : `In ${hours} hours`;
      } else {
        return hours === 1 ? `In 1h ${remainingMins}m` : `In ${hours}h ${remainingMins}m`;
      }
    } else {
      // More than 24 hours away
      const days = Math.floor(minutes / 1440);
      return days === 1 ? 'In 1 day' : `In ${days} days`;
    }
  }
}

export const formatTime = (date: Date) => {
  try {
    const timeStr = new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
    return formatTimeCompact(timeStr);
  } catch (error) {
    console.warn('Failed to format time with toLocaleTimeString, using fallback:', error);
    // Fallback to a simple time format
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    const timeStr = `${displayHours}:${displayMinutes} ${ampm}`;
    return formatTimeCompact(timeStr);
  }
};

export const formatTimeCompact = (timeStr: string): string => {
  // Remove ":00" but keep other minute values like ":15" or ":45"
  return timeStr.replace(/:00/g, '');
};

export const formatCompactTimeRange = (start: Date, end: Date): string => {
  const formatTime = (date: Date, includeAmPm: boolean = true): string => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;

    // Only show minutes if they're not 00
    const displayMinutes = minutes === 0 ? '' : `:${minutes.toString().padStart(2, '0')}`;

    const timeStr = `${displayHours}${displayMinutes}`;
    return includeAmPm ? `${timeStr}${ampm}` : timeStr;
  };

  const startHour = start.getHours();
  const endHour = end.getHours();
  const startMinutes = start.getMinutes();
  const endMinutes = end.getMinutes();

  // If same hour and start is on the hour (00 minutes)
  if (startHour === endHour && startMinutes === 0) {
    const ampm = startHour >= 12 ? 'pm' : 'am';
    const displayHours = startHour % 12 || 12;

    if (endMinutes === 0) {
      // Same hour, both on the hour: "3pm"
      return `${displayHours}${ampm}`;
    } else {
      // Same hour, start on hour, end has minutes: "3-3:30am"
      return `${displayHours}-${displayHours}:${endMinutes.toString().padStart(2, '0')}${ampm}`;
    }
  }

  // Different hours or start has minutes
  const startTime = formatTime(start, false); // Don't include am/pm yet
  const endTime = formatTime(end, true); // Include am/pm on end time

  // Check if we need am/pm on start time
  const startAmPm = startHour >= 12 ? 'pm' : 'am';
  const endAmPm = endHour >= 12 ? 'pm' : 'am';

  // Only show am/pm on start if it's different from end
  const startTimeWithAmPm = startAmPm !== endAmPm ? `${startTime}${startAmPm}` : startTime;

  return `${startTimeWithAmPm}-${endTime}`;
};
export const dateComparisonStr = (date: Date) => {
  try {
    return date.toLocaleDateString('en-US', { dateStyle: 'short' });
  } catch (error) {
    console.warn('Failed to format date with toLocaleDateString, using fallback:', error);
    // Fallback to a simple date format
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }
};
export function getDateStr(inputDate: Date): string {
  const year = inputDate.getFullYear();
  const month = String(inputDate.getMonth() + 1).padStart(2, '0'); // Month is 0-based
  const day = String(inputDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function getDateStrLocal(inputDate: Date | string): string {
  // Fallback implementation that doesn't rely on Intl/DateTimeFormat
  const date = typeof inputDate === 'string' ? new Date(inputDate) : new Date(inputDate);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Convert seconds to HH:MM format
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

export function getTimeStrLocal(inputDate: Date): string {
  try {
    const timeStr = new Date(inputDate).toLocaleTimeString('en-US', {
      hour12: false,
      hourCycle: 'h23',
      hour: '2-digit',
      minute: '2-digit'
    });
    return formatTimeCompact(timeStr);
  } catch (error) {
    console.warn('Failed to format time with toLocaleTimeString, using fallback:', error);
    // Fallback to a simple time format (24-hour)
    const hours = inputDate.getHours().toString().padStart(2, '0');
    const minutes = inputDate.getMinutes().toString().padStart(2, '0');
    const timeStr = `${hours}:${minutes}`;
    return formatTimeCompact(timeStr);
  }
}

export function getLocalDate(inputDate?: Date): Date {
  if (!inputDate) {
    inputDate = new Date();
  }
  const userTimezoneOffset = inputDate.getTimezoneOffset() * 60000;
  return new Date(inputDate.getTime() - userTimezoneOffset);
}

export function fromDateStr(inputDate: string): Date {
  const parseInt10 = (num: string): number => parseInt(num, 10);
  const [year, month, date] = inputDate.split('-').map(parseInt10);
  const myDate = getLocalDate();
  myDate.setFullYear(year);
  myDate.setMonth(month - 1);
  myDate.setDate(date);
  return myDate;
}

export function timeSince(date: Date) {
  return timeBetween(date, new Date());
}

export function dueDateString(dateFrom: Date, dateUntil: Date) {
  if (dateFrom > dateUntil) {
    return `${timeBetween(dateUntil, dateFrom)} ago`;
  } else {
    return `in ${timeBetween(dateFrom, dateUntil)}`;
  }
}

export function timeBetween(dateFrom: Date, dateUntil: Date) {
  // @ts-expect-error date math
  const seconds = Math.floor((dateUntil - dateFrom) / 1000);
  // console.log('dateUntil, dateFrom', {dateFrom, dateUntil, seconds})
  let interval = seconds / 31536000;

  if (interval > 1) {
    return Math.floor(interval) + ' years';
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return Math.floor(interval) + ' months';
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return Math.floor(interval) + ' days';
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return Math.floor(interval) + ' hours';
  }
  interval = seconds / 60;
  if (interval > 1) {
    return Math.floor(interval) + ' minutes';
  }
  return Math.floor(seconds) + ' seconds';
}
export const getDiffHours = (calendarEvent: Partial<CalendarEvent>) => {
  if (!calendarEvent.start || !calendarEvent.end) return 0;
  const start = parseDate(calendarEvent.start);
  const end = parseDate(calendarEvent.end);

  // Calculate the actual difference in hours
  const diff = end.valueOf() - start.valueOf();
  const hours = diff / (60 * 60 * 1000);

  // If the event is longer than 23 hours, treat it as an all-day event
  if (hours >= 23) {
    return 24;
  }

  return hours;
};
export function getPreviousMonday(inputDate: Date): Date {
  const currentDate: Date = new Date(inputDate);
  const currentDay: number = currentDate.getDay();
  // Calculate the number of days to subtract to find the previous Monday
  //  S M T W TH F S
  //  0 1 2 3 4  5 6
  // -1 0 1 2 3  4 5
  const daysToSubtract: number = (7 + (currentDay - 1)) % 7;
  // Subtract the days to get the previous Monday
  currentDate.setDate(currentDate.getDate() - daysToSubtract);

  return currentDate;
}
